import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { connect } from "react-redux";
import { isSameDay, isAfter, parseISO } from "date-fns";
import isISODate from "is-iso-date";

import {
  setDateRange,
  setNumRooms,
  setNumPersons,
  setHotelResults,
  setBackdropValue,
  setInputBoxVisibility,
} from "store";

import { visbook } from "api";
import WP from "WP";
import { scrollWindowTo } from "helpers";
import useIsMobile from "hooks/useIsMobile";

import IconSelector from "partials/IconSelector";
import Button from "partials/Button";
import LoadingButton from "partials/LoadingButton";
import GridElement from "partials/GridElement";
import Container from "partials/Container";
import FavoriteButton from "partials/FavoriteButton";

import DateRangePicker from "./DateRangePicker";
import MobileDateRangePicker from "./MobileRoomAvailabilityDateRangePicker";
import GuestGroup from "./GuestGroup";
import MobileGuestGroup from "./MobileGuestGroup";

import Media from "css/Media";

export const RoomAvailabilityChecker = (props) => {
  const isMobile = useIsMobile();
  const [submitting, setSubmitting] = useState(false);
  const [valid, setValid] = useState(true);
  const { availability, item } = props;
  const open = props.inputBoxVisibility;

  useEffect(() => {
    return () => {
      props.setInputBoxVisibility(false);
    };
  }, []);

  const getDateValidity = () => {
    let today = new Date();
    let isFutureDate;
    let isEqual;
    let isISO = isISODate(availability.fromDate);
    if (isISO) {
      isFutureDate = isAfter(parseISO(availability.fromDate), today);
      isEqual = isSameDay(parseISO(availability.fromDate), today);
    } else {
      isFutureDate = isAfter(availability.fromDate, today);
      isEqual = isSameDay(availability.fromDate, today);
    }
    if (isFutureDate || isEqual) return true;
    return false;
  };

  const getErrorText = () => {
    if (!availability.fromDate || !availability.toDate || !getDateValidity())
      return WP.trans.noDateSpan;
    if (!availability.numRooms) return WP.trans.noNumRooms;
    if (!availability.numPersons) return WP.trans.noNumPersons;
  };

  const handleRoomChange = (name, value) => {
    if (name === "numRooms") {
      props.setNumRooms(value);
    } else if (name === "numPersons") {
      props.setNumPersons(value);
    }
    setValid(true);
  };

  const handleDateChange = (dateObjects, dateStr, i) => {
    if (dateObjects.length > 1) {
      let from = dateObjects[0];
      let to = dateObjects[1];
      let isEqual = isSameDay(from, to);
      if (!isEqual) {
        props.setDateRange(from, to);
      } else {
        props.setDateRange(null, null);
      }
    }
    setValid(true);
  };

  const handleGuestGroupClick = () => {
    props.setBackdropValue(true);
    props.setInputBoxVisibility(true);
  };

  const handleSubmit = () => {
    setSubmitting(true);
    if (
      getDateValidity() &&
      availability.fromDate &&
      availability.toDate &&
      availability.numRooms &&
      availability.numPersons
    ) {
      setValid(true);
      handleVisbookSearch();
    } else {
      setValid(false);
      setSubmitting(false);
    }
  };

  const handleVisbookSearch = () => {
    visbook
      .webproducts({
        fromDate: availability.fromDate,
        toDate: availability.toDate,
        numRooms: availability.numRooms,
        numPersons: availability.numPersons,
      })
      .then((resp) => {
        props.setHotelResults(resp);
        setSubmitting(false);
        props.setInputBoxVisibility(false);
        handleScrollDown();
      });
  };

  const handleScrollDown = () => {
    const destination = document.getElementById("room-types");
    scrollWindowTo(destination, 500, "easeInOutQuad");
  };

  if (!isMobile) {
    return (
      <Wrapper>
        <InnerGrid>
          <GridElement colStart={1} colEnd={20} rowStart={1}>
            <Container flex align='center'>
              <FlexElement first>
                <DateRangePicker
                  fromDate={availability.fromDate}
                  toDate={availability.toDate}
                  valid={valid}
                  dateHandler={(dateObjects, dateStr, instance) =>
                    handleDateChange(dateObjects, dateStr, instance)
                  }
                />
              </FlexElement>
              <FlexElement second>
                <StyledDiv>
                  <IconSelector icon='user' />
                  <InputWrapper>
                    <StyledMockInput
                      valid={valid}
                      onClick={() => handleGuestGroupClick()}
                    >
                      {availability.numRooms && availability.numPersons
                        ? `${availability.numRooms} ${WP.trans.rooms}, ${availability.numPersons} ${WP.trans.guests}`
                        : WP.trans.amountOfGuests}
                    </StyledMockInput>
                    {open && (
                      <GuestGroup
                        numRooms={availability.numRooms}
                        numPersons={availability.numPersons}
                        handleChange={(name, value) =>
                          handleRoomChange(name, value)
                        }
                      />
                    )}
                  </InputWrapper>
                </StyledDiv>
              </FlexElement>
              <FlexElement third>
                {submitting ? (
                  <LoadingButton placeholder={WP.trans.seeAvailableRooms} />
                ) : (
                  <StyledButton disabled={!valid} onClick={handleSubmit}>
                    {WP.trans.seeAvailableRooms}
                  </StyledButton>
                )}
              </FlexElement>
              <FlexElement last>
                <FavoriteButton sort={item.type} item={item} />
              </FlexElement>
            </Container>
          </GridElement>
          {!valid && <ErrorText>{getErrorText()}</ErrorText>}
        </InnerGrid>
      </Wrapper>
    );
  } else {
    return (
      <GridElement colStart={1} colEnd={8} rowStart={1}>
        <InnerGrid fixed={props.fixed}>
          <GridElement colStart={1} colEnd={8} rowStart={1}>
            <MobileSearch>
              <MobileDateRangePicker
                fromDate={availability.fromDate}
                toDate={availability.toDate}
                valid={valid}
                dateHandler={(dateObjects, dateStr, instance) =>
                  handleDateChange(dateObjects, dateStr, instance)
                }
              />
              <MobileMockInput onClick={handleGuestGroupClick}>
                {availability.fromDate && (
                  <p>
                    {availability.numRooms ? (
                      <p>
                        {`${
                          availability.numRooms ? availability.numRooms : "0"
                        } ${WP.trans.rooms.toUpperCase()} - ${
                          availability.numPersons
                            ? availability.numPersons
                            : "0"
                        } ${WP.trans.guests.toUpperCase()}`}
                      </p>
                    ) : (
                      <p>{WP.trans.amountOfRooms}</p>
                    )}
                  </p>
                )}
              </MobileMockInput>
              {open && (
                <MobileGuestGroup
                  numRooms={availability.numRooms}
                  numPersons={availability.numPersons}
                  handleChange={(name, value) => handleRoomChange(name, value)}
                  handleSubmit={handleSubmit}
                  submitting={submitting}
                />
              )}
            </MobileSearch>
          </GridElement>
          <GridElement colStart={1} colEnd={8} rowStart={2}>
            {submitting ? (
              <LoadingButton placeholder={WP.trans.seeAvailableRooms} />
            ) : (
              <StyledButton disabled={!valid} onClick={handleSubmit}>
                {WP.trans.seeAvailableRooms}
              </StyledButton>
            )}
          </GridElement>
          {!valid && <ErrorText>{getErrorText()}</ErrorText>}
        </InnerGrid>
      </GridElement>
    );
  }
};

const mapStateToProps = (state) => ({
  hotels: state.hotels,
  availability: state.availability,
  backdropValue: state.backdropValue,
  inputBoxVisibility: state.inputBoxVisibility,
});

const actions = {
  setDateRange,
  setNumPersons,
  setNumRooms,
  setHotelResults,
  setBackdropValue,
  setInputBoxVisibility,
};

export default connect(mapStateToProps, actions)(RoomAvailabilityChecker);

const Wrapper = styled.div`
  background: ${(props) => props.theme.colors.negative};

  .container {
    padding: 0;
  }
`;

const InnerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr 0.33fr) 1fr;
  grid-template-rows: ${(props) => props.rows || "auto"};
  padding: ${(props) => (props.fixed ? "0" : "0 25px")};

  ${Media.greaterThan("s")`
    padding: 0;
    grid-template-columns: repeat(8, .67fr 1fr);
  `}

  ${Media.greaterThan("m")`
    grid-template-columns: repeat(9, .435fr 1fr) .435fr;
  `}
`;

const FlexElement = styled.div`
  ${(props) =>
    props.first &&
    css`
      width: 100%;
      margin-right: ${(props) => props.theme.spacing.contentXL};
      flex: 1;
    `}

  ${(props) =>
    props.second &&
    css`
      flex: 1;
      margin-right: ${(props) => props.theme.spacing.contentXS};
    `}
    ${(props) =>
    props.third &&
    css`
      margin-right: ${(props) => props.theme.spacing.elementM};
    `}

    ${(props) =>
    !props.first &&
    css`
      width: auto;
    `}
`;

const StyledDiv = styled.div`
  display: flex;
  width: 100%;
`;

const StyledButton = styled(Button)`
  ${Media.lessThan("s")`
        padding: ${(props) => props.theme.spacing.contentS};
    `}
`;

const StyledMockInput = styled.div`
  cursor: pointer;
  box-sizing: border-box;
  padding: ${(props) => props.theme.spacing.contentXS};
  font-size: ${(props) => props.theme.spacing.contentS};
  border-bottom: 1px solid
    ${(props) =>
      props.valid ? props.theme.colors.lines : props.theme.colors.button};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  color: ${(props) =>
    props.valid ? props.theme.colors.text : props.theme.colors.button};
`;

const MobileMockInput = styled.div`
  cursor: pointer;
  box-sizing: border-box;
  padding: 0;
  white-space: nowrap;
  margin-bottom: 30px;

  p {
    font-size: 11px;
    line-height: 20px;
    font-weight: 600;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-right: ${(props) => props.theme.spacing.contentXL};
`;

const MobileSearch = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorText = styled.p`
  margin-top: 16px;
  background-color: ${(props) => props.theme.colors.tooltip};
  padding: ${(props) => props.theme.spacing.contentL};
  border-radius: ${(props) => props.theme.spacing.contentM};
  grid-column: 1/8;
  grid-row: 4;

  ${Media.greaterThan("s")`
    grid-column: 14/22;
    grid-row: auto;
  `}
`;
